import type { DependencyGraph, DependencyEdge, Service } from "@coremonit/types";
import { SERVICE_KIND } from "./services";
import { makeRng, int, chance } from "./rng";

// Hand-crafted realistic dependency spine — matches the ordering in services.ts.
// Adjacency: from → to. Missing services get pulled in as leaves.
const SPINE: Array<[string, string]> = [
  ["web-storefront", "api-gateway"],
  ["mobile-bff",     "api-gateway"],
  ["api-gateway",    "auth-service"],
  ["api-gateway",    "user-service"],
  ["api-gateway",    "order-service"],
  ["api-gateway",    "search-service"],
  ["auth-service",   "user-service"],
  ["auth-service",   "session-cache"],
  ["order-service",  "inventory-service"],
  ["order-service",  "payment-service"],
  ["order-service",  "notification-service"],
  ["order-service",  "kafka-events"],
  ["order-service",  "postgres-primary"],
  ["payment-service","stripe-webhook"],
  ["payment-service","billing-service"],
  ["inventory-service","postgres-primary"],
  ["inventory-service","postgres-replica"],
  ["user-service",   "postgres-primary"],
  ["notification-service","email-worker"],
  ["notification-service","sendgrid-webhook"],
  ["search-service", "postgres-replica"],
  ["email-worker",   "kafka-events"],
  ["reporting-service","postgres-replica"],
  ["recommendation-svc","postgres-replica"],
];

export function buildDependencyGraph(services: Service[], seed = 42): DependencyGraph {
  const rng = makeRng(seed);
  const byName = new Map(services.map((s) => [s.name, s]));
  const nodes = services.map((s) => ({
    id: s.id, name: s.name, status: s.status,
    kind: (SERVICE_KIND[s.id] ?? "backend") as DependencyGraph["services"][number]["kind"],
  }));
  const edges: DependencyEdge[] = [];
  for (const [from, to] of SPINE) {
    const a = byName.get(from);
    const b = byName.get(to);
    if (!a || !b) continue;
    const rpm = int(rng, 50, Math.max(80, Math.min(a.requestsPerMin, b.requestsPerMin)));
    edges.push({
      fromServiceId: a.id,
      toServiceId: b.id,
      requestsPerMin: rpm,
      errorRatePct: +(Math.max(a.errorRatePct, b.errorRatePct) * 0.6).toFixed(2),
      latencyP95Ms: Math.max(a.latencyP95Ms, b.latencyP95Ms),
      critical: a.tags.includes("critical") || b.tags.includes("critical"),
    });
  }
  return { services: nodes, edges };
}
